import React from "react";
import { Button, Input } from "reactstrap";
import { ChevronDown, ChevronUp } from "react-feather";
import { C } from "../../reporting/reportingStyles";
import {
    ALIGN_OPTIONS,
    BARE_KEYS,
    BARE_SIZE_OPTIONS,
    LOCKED_KEYS,
    MAX_TEXT_LENGTH,
    MAX_TEXT_ROWS,
    ROW_LABELS,
    SIZE_OPTIONS,
} from "./receiptLayoutMeta";

const sectionTitle = { fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 8 };
const rowBox = {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 8,
    padding: "8px 10px",
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    marginBottom: 6,
    background: C.white,
};
const iconBtn = {
    border: `1px solid ${C.border}`,
    background: C.white,
    borderRadius: 6,
    width: 26,
    height: 26,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    cursor: "pointer",
};
const checkLabel = { display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, color: C.textMed, margin: 0 };

const newTextId = (section) => `${section === "header" ? "h" : "f"}-${Date.now().toString(36)}`;

/**
 * Edits a receipt layout (see camel-step-ecommerce-api/src/util/receiptLayout.js for the shape).
 *
 * Controlled: `value` is always a complete layout — the API hands the page a resolved one — and every
 * change goes back through `onChange` with a new object.
 */
const ReceiptLayoutEditor = ({ value, onChange }) => {
    if (!value) return null;

    const setSection = (section, rows) => onChange({ ...value, [section]: rows });

    const updateRow = (section, index, patch) =>
        setSection(section, value[section].map((r, i) => (i === index ? { ...r, ...patch } : r)));

    const move = (section, index, dir) => {
        const rows = [...value[section]];
        const to = index + dir;
        if (to < 0 || to >= rows.length) return;
        [rows[index], rows[to]] = [rows[to], rows[index]];
        setSection(section, rows);
    };

    const addText = (section) =>
        setSection(section, [
            ...value[section],
            { key: "text", id: newTextId(section), visible: true, size: "sm", bold: false, align: "center", en: "", ar: "" },
        ]);

    const removeRow = (section, index) => setSection(section, value[section].filter((_, i) => i !== index));

    const updateItems = (patch) => onChange({ ...value, items: { ...value.items, ...patch } });

    const renderRow = (section, row, index, count) => {
        const locked = LOCKED_KEYS.includes(row.key);
        const bare = BARE_KEYS.includes(row.key);
        const isText = row.key === "text";

        return (
            <div key={isText ? row.id : row.key} style={{ ...rowBox, opacity: row.visible ? 1 : 0.6 }}>
                <div style={{ display: "flex", gap: 4 }}>
                    <button type="button" style={iconBtn} disabled={index === 0} onClick={() => move(section, index, -1)} title="Move up">
                        <ChevronUp size={14} />
                    </button>
                    <button type="button" style={iconBtn} disabled={index === count - 1} onClick={() => move(section, index, 1)} title="Move down">
                        <ChevronDown size={14} />
                    </button>
                </div>

                <div style={{ minWidth: 120, fontSize: 13, fontWeight: 500, color: C.text }}>
                    {ROW_LABELS[row.key] || row.key}
                    {locked && (
                        <div style={{ fontSize: 11, color: C.textLight, fontWeight: 400 }}>Required on a tax invoice</div>
                    )}
                </div>

                <label style={checkLabel} title={locked ? "Required on a tax invoice — it cannot be hidden" : undefined}>
                    <input
                        type="checkbox"
                        checked={!!row.visible}
                        disabled={locked}
                        onChange={(e) => updateRow(section, index, { visible: e.target.checked })}
                        style={{ accentColor: C.coral }}
                    />
                    Show
                </label>

                {bare && (
                    <Input
                        type="select"
                        bsSize="sm"
                        value={row.size || (row.key === "logo" ? "lg" : "md")}
                        onChange={(e) => updateRow(section, index, { size: e.target.value })}
                        style={{ width: 120 }}
                        title={row.key === "logo" ? "Logo size" : "QR code size"}
                    >
                        {BARE_SIZE_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                    </Input>
                )}

                {!bare && (
                    <>
                        <Input
                            type="select"
                            bsSize="sm"
                            value={row.size}
                            onChange={(e) => updateRow(section, index, { size: e.target.value })}
                            style={{ width: 120 }}
                            title="Text size"
                        >
                            {SIZE_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </Input>
                        <Input
                            type="select"
                            bsSize="sm"
                            value={row.align}
                            onChange={(e) => updateRow(section, index, { align: e.target.value })}
                            style={{ width: 95 }}
                            title="Alignment"
                        >
                            {ALIGN_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </Input>
                        <label style={checkLabel}>
                            <input
                                type="checkbox"
                                checked={!!row.bold}
                                onChange={(e) => updateRow(section, index, { bold: e.target.checked })}
                                style={{ accentColor: C.coral }}
                            />
                            Bold
                        </label>
                    </>
                )}

                {isText && (
                    <>
                        <div style={{ flexBasis: "100%", display: "flex", gap: 8 }}>
                            <Input
                                bsSize="sm"
                                placeholder="English text"
                                maxLength={MAX_TEXT_LENGTH}
                                value={row.en || ""}
                                onChange={(e) => updateRow(section, index, { en: e.target.value })}
                            />
                            <Input
                                bsSize="sm"
                                dir="rtl"
                                placeholder="النص العربي"
                                maxLength={MAX_TEXT_LENGTH}
                                value={row.ar || ""}
                                onChange={(e) => updateRow(section, index, { ar: e.target.value })}
                            />
                            <Button color="danger" size="sm" onClick={() => removeRow(section, index)} title="Remove">
                                ✕
                            </Button>
                        </div>
                    </>
                )}
            </div>
        );
    };

    const renderSection = (section, title, hint) => {
        const rows = value[section] || [];
        const textCount = rows.filter((r) => r.key === "text").length;
        return (
            <div style={{ marginBottom: 18 }}>
                <div style={sectionTitle}>{title}</div>
                <div style={{ fontSize: 12, color: C.textLight, marginBottom: 8 }}>{hint}</div>
                {rows.map((row, i) => renderRow(section, row, i, rows.length))}
                {textCount < MAX_TEXT_ROWS && (
                    <Button
                        color="outline-primary"
                        size="sm"
                        className="mt-1 d-flex align-items-center gap-1"
                        onClick={() => addText(section)}
                    >
                        <i className="fa fa-plus"></i> Add custom line
                    </Button>
                )}
            </div>
        );
    };

    const items = value.items || {};
    const prefix = value.callNumber?.prefix || "";
    const setPrefix = (raw) =>
        onChange({
            ...value,
            // Letters and digits only, three at most — the same rule the API enforces.
            callNumber: { prefix: raw.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 3) },
        });

    return (
        <div>
            <div style={{ marginBottom: 18 }}>
                <div style={sectionTitle}>Call number</div>
                <div style={{ ...rowBox, gap: 12 }}>
                    <label style={{ ...checkLabel, color: C.text, fontSize: 13, fontWeight: 500 }}>
                        <input
                            type="checkbox"
                            checked={!!prefix}
                            onChange={(e) => setPrefix(e.target.checked ? "K" : "")}
                            style={{ accentColor: C.coral }}
                        />
                        Show a prefix
                    </label>
                    {!!prefix && (
                        <Input
                            bsSize="sm"
                            value={prefix}
                            onChange={(e) => setPrefix(e.target.value)}
                            maxLength={3}
                            placeholder="K"
                            style={{ width: 80, textTransform: "uppercase" }}
                            title="Up to 3 letters or digits"
                        />
                    )}
                    <span style={{ fontSize: 12, color: C.textMed }}>
                        Prints as <strong>{prefix ? `${prefix}-2671` : "2671"}</strong>
                    </span>
                </div>
                <div style={{ fontSize: 12, color: C.textLight }}>
                    The number customers are called by — on the receipt, the kitchen ticket, the kitchen screen and the
                    order confirmation. It is the last four digits of the order number.
                </div>
            </div>

            {renderSection("header", "Header", "Printed above the call number, top to bottom in this order.")}

            <div style={{ marginBottom: 18 }}>
                <div style={sectionTitle}>Item lines</div>
                <div style={{ ...rowBox, gap: 14 }}>
                    <Input
                        type="select"
                        bsSize="sm"
                        value={items.size}
                        onChange={(e) => updateItems({ size: e.target.value })}
                        style={{ width: 120 }}
                        title="Text size"
                    >
                        {SIZE_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                    </Input>
                    {[
                        ["showArabicName", "Arabic names"],
                        ["showModifiers", "Add-ons"],
                        ["showNote", "Item notes"],
                    ].map(([flag, label]) => (
                        <label key={flag} style={checkLabel}>
                            <input
                                type="checkbox"
                                checked={!!items[flag]}
                                onChange={(e) => updateItems({ [flag]: e.target.checked })}
                                style={{ accentColor: C.coral }}
                            />
                            {label}
                        </label>
                    ))}
                </div>
                <div style={{ fontSize: 12, color: C.textLight }}>
                    The call number, order details, totals and payment lines are fixed and always print.
                </div>
            </div>

            {renderSection("footer", "Footer", "Printed after the payment lines. Lines below the QR code print after it.")}
        </div>
    );
};

export default ReceiptLayoutEditor;
